import { useEffect, useState } from "react";
import { avatarUrl } from "@/lib/avatars";
import { audio } from "@/lib/audio";
import { Volume2, VolumeX, Settings2 } from "lucide-react";

export function PaperButton({ children, className = "", color, sound = "click", onClick, disabled, ...props }) {
  const bg = color || "#FDFBF7";
  return (
    <button
      className={`btn-sketch px-5 py-3 text-lg focus-mark ${className}`}
      style={{ background: bg }}
      disabled={disabled}
      onMouseEnter={() => audio.play("hover")}
      onClick={(e) => { if (!disabled) { audio.play(sound); onClick && onClick(e); } }}
      {...props}
    >
      {children}
    </button>
  );
}

export function Avatar({ seed, size = 56, className = "", ring }) {
  const [err, setErr] = useState(false);
  const initial = (seed || "?").replace(/^[a-z\-]+:/, "").slice(0, 1).toUpperCase();
  if (err) {
    return (
      <div
        className={`flex items-center justify-center font-hand font-bold ${className}`}
        style={{ width: size, height: size, background: "#FFE9A8", border: "2px solid #1C1B1A", borderRadius: 10, fontSize: size * 0.42 }}
      >{initial}</div>
    );
  }
  return (
    <img
      src={avatarUrl(seed)}
      alt="avatar"
      onError={() => setErr(true)}
      className={className}
      style={{ width: size, height: size, border: "2px solid #1C1B1A", borderRadius: 10, background: "#FDFBF7", boxShadow: ring ? `0 0 0 3px ${ring}` : "none" }}
    />
  );
}

export function Timer({ endsAt, className = "", warnSound = true }) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!endsAt) { setLeft(0); return; }
    let lastBeep = null;
    const tick = () => {
      const ms = Math.max(0, endsAt - Date.now());
      const s = Math.ceil(ms / 1000);
      setLeft(s);
      if (warnSound && s <= 5 && s > 0 && lastBeep !== s) { lastBeep = s; audio.play("countdown"); }
    };
    tick();
    const iv = setInterval(tick, 200);
    return () => clearInterval(iv);
  }, [endsAt, warnSound]);
  if (!endsAt) return null;
  const danger = left <= 5;
  return (
    <div
      className={`font-hand font-bold tabular-nums ${className}`}
      style={{ color: danger ? "#E23B44" : "#1C1B1A" }}
    >{left}</div>
  );
}

export function RoleBadge({ label, icon, color, className = "" }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-hand font-bold px-3 py-1 ${className}`}
      style={{ background: "#FDFBF7", border: `2px solid ${color || "#1C1B1A"}`, color: color || "#1C1B1A", borderRadius: "6px 12px 6px 12px" }}
    >
      <span>{icon}</span>{label}
    </span>
  );
}

export function AudioSettings() {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(audio.get());
  const upd = (k, v) => { audio.set(k, v); setS(audio.get()); };
  return (
    <div className="relative">
      <button
        data-testid="audio-settings-btn"
        onClick={() => { audio.play("click"); setOpen((o) => !o); }}
        className="btn-sketch p-2.5"
        title="Sound settings"
        aria-label="Sound settings"
      >
        {s.muted ? <VolumeX size={20} strokeWidth={2.5} /> : <Volume2 size={20} strokeWidth={2.5} />}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 z-50 card-paper p-4 animate-pop-in" data-testid="audio-panel">
          <div className="flex items-center gap-2 mb-3 font-hand font-bold text-lg"><Settings2 size={18} strokeWidth={2.5} /> Sound</div>
          {[["master", "Master"], ["music", "Music"], ["sfx", "Effects"]].map(([k, lbl]) => (
            <div key={k} className="mb-3">
              <div className="flex justify-between text-sm font-semibold"><span>{lbl}</span><span>{Math.round(s[k] * 100)}%</span></div>
              <input
                type="range" min="0" max="1" step="0.05" value={s[k]}
                data-testid={`vol-${k}`}
                onChange={(e) => upd(k, parseFloat(e.target.value))}
                className="w-full accent-marker-blue"
              />
            </div>
          ))}
          <label className="flex items-center gap-2 mt-2 cursor-pointer font-semibold text-sm">
            <input type="checkbox" checked={s.muted} data-testid="mute-all" onChange={(e) => upd("muted", e.target.checked)} />
            Mute all
          </label>
        </div>
      )}
    </div>
  );
}

export function Doodle({ children, className = "", rotate = 0, color = "#FDFBF7" }) {
  return (
    <div className={`card-paper p-5 ${className}`} style={{ transform: `rotate(${rotate}deg)`, background: color }}>
      {children}
    </div>
  );
}
